import React, { useState, useEffect, useRef, useMemo } from "react";
import { Project, ProjectMedia } from "@/types";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBatchData } from "@/hooks/useBatchData";
import { useQueries } from "@tanstack/react-query";
import { fetchProjectMedia } from "@/services/api";
import ProjectCardHeader from "./ProjectCardHeader";
import ProjectMeasuresTabs from "./ProjectMeasuresTabs";
import ProjectMediaThumbnailGallery from "../ProjectMediaThumbnailGallery";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogClose,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import ProjectMediaFullScreenSlider from "@/components/ProjectMediaFullScreenSlider";
import { createPortal } from "react-dom";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import ProjectInfo from "./ProjectInfo";
import ProjectMediaCarousel from "./ProjectMediaCarousel";

interface ProjectCardListProps {
  projects: Project[];
  selectedProject: Project | null;
  onSelectProject: (project: Project) => void;
  batchTitles: Record<string, string>;
  projectToBatchIds: Record<string, string[]>;
  customerColor: string;
  isLoadingBatchData?: boolean;
}

const ProjectCardList: React.FC<ProjectCardListProps> = ({
  projects,
  selectedProject,
  onSelectProject,
  batchTitles,
  projectToBatchIds,
  customerColor,
  isLoadingBatchData = false,
}) => {
  const { t } = useLanguage();
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [selectedMediaProject, setSelectedMediaProject] = useState<
    string | null
  >(null);
  const projectRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [fullScreenImageIndex, setFullScreenImageIndex] = useState<
    number | null
  >(null);
  const isMobile = useIsMobile();

  const projectMediaQueries = useQueries({
    queries: projects.map((project) => ({
      queryKey: ["project-media", project.id],
      queryFn: () => fetchProjectMedia(project.id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const projectMediaMap = useMemo<Record<string, ProjectMedia[]>>(() => {
    const mediaMap: Record<string, ProjectMedia[]> = {};
    projects.forEach((project, index) => {
      mediaMap[project.id] = projectMediaQueries[index]?.data ?? [];
    });
    return mediaMap;
  }, [projects, projectMediaQueries]);

  useEffect(() => {
    if (selectedProject) {
      setOpenProjectId(selectedProject.id);
      const projectCardElement = projectRefs.current[selectedProject.id];

      setTimeout(() => {
        if (projectCardElement) {
          const headerOffset = 100;
          const elementPosition = projectCardElement.offsetTop;
          window.scrollTo({
            top: elementPosition - headerOffset,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [selectedProject]);

  const handleToggleProject = (project: Project, isOpen: boolean) => {
    if (isOpen) {
      onSelectProject(project);
      setOpenProjectId(project.id);
    } else {
      setOpenProjectId(null);
    }
  };

  const handleImageClick = (projectId: string, index: number) => {
    if (isMobile) {
      setSelectedMediaProject(projectId);
      setFullScreenImageIndex(index);
      setIsFullScreenOpen(true);
    } else {
      setSelectedMediaProject(projectId);
      setIsGalleryOpen(true);
    }
  };

  const handleFullScreenOpen = (index: number) => {
    setFullScreenImageIndex(index);
    setIsFullScreenOpen(true);
    setIsGalleryOpen(false);
  };

  const handleFullScreenClose = () => {
    setFullScreenImageIndex(null);
    setIsFullScreenOpen(false);
    if (!isMobile) {
      setIsGalleryOpen(true);
    }
  };

  // Get batch data for selected project
  const { projectMeasurements, isLoading } = useBatchData(
    selectedProject?.id || null,
    selectedProject ? projectToBatchIds[selectedProject.id] || [] : [],
    batchTitles
  );

  return (
    <div className="w-full">
      <div className="space-y-4">
        {projects.map((project) => {
          const isOpen = openProjectId === project.id;
          const isSelected = selectedProject?.id === project.id;
          const batchIds = projectToBatchIds[project.id] || [];
          const projectMedia = projectMediaMap[project.id] || [];

          return (
            <Card
              ref={(el) => (projectRefs.current[project.id] = el)}
              key={project.id}
              className={`transition-all duration-200 bg-gray-50 ${
                isSelected ? "border-2" : "border"
              }`}
              style={{
                borderColor: isSelected ? customerColor : "",
              }}
            >
              <Collapsible
                open={isOpen}
                onOpenChange={(open) => handleToggleProject(project, open)}
              >
                <CollapsibleTrigger className="w-full text-left">
                  <ProjectCardHeader
                    project={project}
                    isOpen={isOpen}
                    projectMedia={projectMedia}
                    onImageClick={(index) =>
                      handleImageClick(project.id, index)
                    }
                    onClick={() => handleToggleProject(project, true)}
                  />
                </CollapsibleTrigger>

                {!isMobile && (
                  <CollapsibleContent className="px-4 pb-4 pt-2 border-t">
                    <div className="mt-6">
                      <h4 className="text-base font-medium mb-3">
                        {getProjectMeasuresHeading(
                          projectMeasurements.planned,
                          projectMeasurements.documented,
                          t
                        )}
                      </h4>

                      {batchIds.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">
                          {t("dashboard.projectCardList.noData")}
                        </p>
                      ) : (
                        <ProjectMeasuresTabs
                          plannedMeasurements={projectMeasurements.planned}
                          documentedMeasurements={
                            projectMeasurements.documented
                          }
                          isLoading={isLoading || isLoadingBatchData}
                          customerColor={customerColor}
                        />
                      )}
                    </div>
                  </CollapsibleContent>
                )}
              </Collapsible>
            </Card>
          );
        })}
      </div>

      {/* Mobile Bottom Sheet for Project Details */}
      {isMobile && selectedProject && (
        <Sheet
          open={!!selectedProject}
          onOpenChange={() => onSelectProject(null)}
        >
          <SheetContent side="bottom" className="h-[90vh]">
            <div className="pt-4 pb-4 overflow-y-auto h-full">
              <ProjectInfo
                project={selectedProject}
                displayInSheet={true}
              />

              {projectMediaMap[selectedProject.id] &&
                projectMediaMap[selectedProject.id].length > 0 && (
                  <div className="mt-4">
                    <ProjectMediaCarousel
                      projectId={selectedProject.id}
                      projectMedia={projectMediaMap[selectedProject.id]}
                      onImageClick={(index) =>
                        handleImageClick(selectedProject.id, index)
                      }
                      noPaddingX={true}
                    />
                  </div>
                )}

              <div className="mt-6">
                <h4 className="text-base font-medium mb-3">
                  {getProjectMeasuresHeading(
                    projectMeasurements.planned,
                    projectMeasurements.documented,
                    t
                  )}
                </h4>

                {projectToBatchIds[selectedProject.id]?.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    {t("dashboard.projectCardList.noData")}
                  </p>
                ) : (
                  <ProjectMeasuresTabs
                    plannedMeasurements={projectMeasurements.planned}
                    documentedMeasurements={projectMeasurements.documented}
                    isLoading={isLoading || isLoadingBatchData}
                    customerColor={customerColor}
                  />
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* High-Quality Image Gallery View in Dialog */}
      <Dialog open={isGalleryOpen} onOpenChange={setIsGalleryOpen}>
        <DialogOverlay />
        <DialogContent
          style={{
            width: "90%",
            height: "90%",
            maxWidth: "100vw",
            maxHeight: "100vh",
            margin: "auto",
            overflow: "auto",
          }}
        >
          <DialogClose asChild>
            <button className="absolute right-4 top-4">
              <span className="sr-only">Close</span>
              <X className="h-4 w-4" />
            </button>
          </DialogClose>
          {selectedMediaProject !== null &&
            projectMediaMap[selectedMediaProject] && (
              <ProjectMediaThumbnailGallery
                projectId={selectedMediaProject}
                media={projectMediaMap[selectedMediaProject]}
                onImageClick={(index) => handleFullScreenOpen(index)}
              />
            )}
        </DialogContent>
      </Dialog>

      {/* Full Screen Image Slider */}
      {isFullScreenOpen &&
        fullScreenImageIndex !== null &&
        selectedMediaProject !== null &&
        createPortal(
          <ProjectMediaFullScreenSlider
            projectId={selectedMediaProject}
            media={projectMediaMap[selectedMediaProject]}
            initialIndex={fullScreenImageIndex}
            onClose={handleFullScreenClose}
          />,
          document.body
        )}
    </div>
  );
};

// Helper function to get the appropriate heading based on available measures
const getProjectMeasuresHeading = (
  plannedMeasurements: any[],
  documentedMeasurements: any[],
  t: (key: string) => string
): string => {
  const hasPlannedMeasures = plannedMeasurements.length > 0;
  const hasDocumentedMeasures = documentedMeasurements.length > 0;

  if (!hasPlannedMeasures && !hasDocumentedMeasures) {
    return t("dashboard.projectCardList.projectMeasures");
  }

  if (hasPlannedMeasures && !hasDocumentedMeasures) {
    return t("dashboard.projectCardList.plannedProjectMeasures");
  }

  if (!hasPlannedMeasures && hasDocumentedMeasures) {
    return t("dashboard.projectCardList.documentedProjectMeasures");
  }

  // Both types exist
  return t("dashboard.projectCardList.projectMeasures");
};

export default ProjectCardList;
